import { ref } from 'vue'
import { 
  collection, 
  doc, 
  addDoc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc,
  query, 
  where, 
  orderBy,
  serverTimestamp 
} from 'firebase/firestore'
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage'
import { db, storage } from '@/services/firebase'
import imageCompression from 'browser-image-compression'

export function usePartners() {
  const loading = ref(false)
  const error = ref(null)

  // 이미지 압축 (2:1 비율 권장)
  const compressImage = async (file) => {
    try {
      const options = {
        maxSizeMB: 0.3,           // 최대 300KB (로고는 작게)
        maxWidthOrHeight: 800,    // 최대 800px
        useWebWorker: true,
        fileType: 'image/webp'    // WebP로 변환
      }
      
      const compressedFile = await imageCompression(file, options)
      
      console.log('원본 크기:', (file.size / 1024).toFixed(2), 'KB')
      console.log('압축 후:', (compressedFile.size / 1024).toFixed(2), 'KB')
      
      return compressedFile
    } catch (e) {
      console.error('이미지 압축 실패:', e)
      return file
    }
  }

  // 로고 업로드 (2:1 비율 권장)
  const uploadLogo = async (file) => {
    const compressedFile = await compressImage(file)
    
    const timestamp = Date.now()
    const safeName = file.name.replace(/[^a-zA-Z0-9]/g, '_').replace(/\.[^.]+$/, '')
    const path = `partners/${timestamp}_${safeName}.webp`
    
    const imageRef = storageRef(storage, path)
    const snapshot = await uploadBytes(imageRef, compressedFile)
    const downloadURL = await getDownloadURL(snapshot.ref)
    
    return downloadURL
  }

  // 활성화된 파트너 조회 (홈페이지용)
  const getPartners = async (limitCount = 8) => {
    loading.value = true
    error.value = null
    try {
      const q = query(
        collection(db, 'partners'),
        where('isActive', '==', true),
        orderBy('order', 'asc')
      )
      const querySnapshot = await getDocs(q)
      
      const partners = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      
      return limitCount ? partners.slice(0, limitCount) : partners
    } catch (e) {
      error.value = '파트너 목록을 불러오는데 실패했습니다.'
      console.error(e)
      return []
    } finally {
      loading.value = false
    }
  }

  // 전체 파트너 조회 (관리자용)
  const getAllPartners = async () => {
    loading.value = true
    error.value = null
    try {
      const q = query(
        collection(db, 'partners'),
        orderBy('order', 'asc')
      )
      const querySnapshot = await getDocs(q)
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
    } catch (e) {
      error.value = '파트너 목록을 불러오는데 실패했습니다.'
      throw e
    } finally {
      loading.value = false
    }
  }

  // 파트너 추가
  const addPartner = async (partnerData) => {
    loading.value = true
    error.value = null
    try {
      const docRef = await addDoc(collection(db, 'partners'), {
        ...partnerData,
        createdAt: serverTimestamp()
      })
      return docRef.id
    } catch (e) {
      error.value = '파트너 추가에 실패했습니다.'
      throw e
    } finally {
      loading.value = false
    }
  }

  // 파트너 수정
  const updatePartner = async (partnerId, partnerData) => {
    loading.value = true
    error.value = null
    try {
      await updateDoc(doc(db, 'partners', partnerId), partnerData)
    } catch (e) {
      error.value = '파트너 수정에 실패했습니다.'
      throw e
    } finally {
      loading.value = false
    }
  }

  // 파트너 삭제
  const deletePartner = async (partnerId) => {
    loading.value = true
    error.value = null
    try {
      await deleteDoc(doc(db, 'partners', partnerId))
    } catch (e) {
      error.value = '파트너 삭제에 실패했습니다.'
      throw e
    } finally {
      loading.value = false
    }
  }

  // 파트너 상세 조회
  const getPartner = async (partnerId) => {
    loading.value = true
    error.value = null
    try {
      const docSnap = await getDoc(doc(db, 'partners', partnerId))
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() }
      } else {
        error.value = '존재하지 않는 파트너입니다.'
        return null
      }
    } catch (e) {
      error.value = '파트너를 불러오는데 실패했습니다.'
      throw e
    } finally {
      loading.value = false
    }
  }

  return {
    loading,
    error,
    compressImage,
    uploadLogo,
    getPartners,
    getAllPartners,
    addPartner,
    updatePartner,
    deletePartner,
    getPartner
  }
}

// 파트너 카테고리
export const PARTNER_CATEGORIES = [
  '종합광고',
  '문자광고',
  '홈페이지제작',
  '부동산중개',
  '디지털콘텐츠',
  '분양마케팅',
  '법률상담',
  '세무회계',
  '인테리어',
  '사진/영상'
]